import { cva } from "class-variance-authority";

export { default as Button } from "./Button.vue";
export { default as IconButton } from "./IconButton.vue";
export { default as LinkButton } from "./LinkButton.vue";
export { default as CloseButton } from "./CloseButton.vue";

export const sizes = ["2xs", "xs", "sm", "md", "lg", "xl", "2xl"];

export const variants = cva(
    [
        "inline-flex",
        "items-center",
        "justify-center",
        "rounded-lg",
        "transition-colors",
        "font-semibold",
        "disabled:fg-disabled",
    ],
    {
        variants: {
            variant: {
                primary: [
                    "border",
                    "button-primary-fg",
                    "button-primary-bg",
                    "button-primary-border",
                    "hover:button-primary-bg_hover",
                    "hover:button-primary-fg_hover",
                    "hover:button-primary-border_hover",
                    "focus:ring-brand-shadow",
                    "disabled:bg-disabled",
                    "disabled:border-disabled_subtle",
                ],
                secondary: [
                    "border",
                    "disabled:bg-primary",
                    "disabled:border-disabled_subtle",
                ],
                tertiary: ["disabled:bg-transparent"],
                link: [
                    "bg-transparent",
                    "border-none",
                    "hover:bg-transparent",
                    "focus:ring-transparent",
                    "disabled:bg-transparent",
                ],
                close: [
                    "fg-quinary",
                    "hover:fg-quinary_hover",
                    "hover:bg-primary_hover",
                    "focus:ring-gray",
                ],
            },
            size: {
                "2xs": [
                    "h-[30px]",
                    "px-2",
                    "text-style-xs",
                    "gap-1",
                    "[&>i]:h-3.5",
                    "[&>i]:w-3.5",
                    "rounded-md",
                ],
                xs: [
                    "h-[30px]",
                    "px-2",
                    "text-style-sm",
                    "gap-1",
                    "[&>i]:h-4",
                    "[&>i]:w-4",
                ],
                sm: [
                    "h-[36px]",
                    "px-3",
                    "text-style-sm",
                    "gap-1.5",
                    "[&>i]:h-4.5",
                    "[&>i]:w-4.5",
                ],
                md: [
                    "h-[40px]",
                    "px-3.5",
                    "text-style-sm",
                    "gap-1.5",
                    "[&>i]:h-5",
                    "[&>i]:w-5",
                ],
                lg: [
                    "h-[44px]",
                    "px-4",
                    "text-style-base",
                    "gap-2",
                    "[&>i]:h-5",
                    "[&>i]:w-5",
                ],
                xl: [
                    "h-[48px]",
                    "px-4.5",
                    "text-style-base",
                    "gap-2",
                    "[&>i]:h-5",
                    "[&>i]:w-5",
                ],
                "2xl": [
                    "h-[56px]",
                    "px-5.5",
                    "text-style-lg",
                    "gap-3",
                    "[&>i]:h-6",
                    "[&>i]:w-6",
                ],
            },
        },
        compoundVariants: [
            {
                variant: "secondary",
                type: "gray",
                class: [
                    "button-secondary-gray-fg",
                    "button-secondary-gray-bg",
                    "button-secondary-gray-border",
                    "focus:ring-gray-shadow",
                    "hover:button-secondary-gray-bg_hover",
                    "hover:button-secondary-gray-fg_hover",
                    "hover:button-secondary-gray-border_hover",
                ],
            },
            {
                variant: "secondary",
                type: "filled",
                size: ["xs", "sm", "md", "lg", "xl", "2xl"],
                class: [
                    "button-secondary-color-fg",
                    "button-secondary-color-bg",
                    "button-secondary-color-border",
                    "focus:ring-brand-shadow",
                    "hover:button-secondary-color-bg_hover",
                    "hover:button-secondary-color-fg_hover",
                    "hover:button-secondary-color-border_hover",
                ],
            },
            {
                variant: "secondary",
                type: "filled",
                size: "2xs",
                class: [
                    "h-5",
                    "w-5",
                    "fg-quaternary",
                    "bg-quaternary",
                    "hover:fg-quaternary_hover",
                    "hover:bg-quaternary_hover_hover",
                    "border-none",
                ],
            },
            {
                variant: "secondary",
                type: "filled",
                size: "2xs",
                icon: true,
                class: ["rounded-5xl"],
            },
            {
                variant: "tertiary",
                type: "gray",
                class: [
                    "button-tertiary-gray-fg",
                    "hover:button-tertiary-gray-bg_hover",
                    "hover:button-tertiary-gray-fg_hover",
                ],
            },
            {
                variant: "tertiary",
                type: "color",
                class: [
                    "button-tertiary-color-fg",
                    "hover:button-tertiary-color-bg_hover",
                    "hover:button-tertiary-color-fg_hover",
                ],
            },
            {
                variant: "tertiary",
                type: "white",
                class: [
                    "button-tertiary-white-fg",
                    "hover:button-tertiary-white-bg_hover",
                    "hover:button-tertiary-white-fg_hover",
                ],
            },
            {
                variant: "link",
                type: "gray",
                class: [
                    "button-tertiary-gray-fg",
                    "hover:button-tertiary-gray-fg_hover",
                ],
            },
            {
                variant: "link",
                type: "color",
                class: [
                    "button-tertiary-color-fg",
                    "hover:button-tertiary-color-fg_hover",
                ],
            },
            {
                variant: "primary",
                type: "success",
                class: [
                    "button-primary-success-fg",
                    "button-primary-success-bg",
                    "button-primary-success-border",
                    "hover:button-primary-success-fg_hover",
                    "hover:button-primary-success-bg_hover",
                    "hover:button-primary-success-border_hover",
                ],
            },
            {
                variant: "secondary",
                type: "success",
                class: [
                    "button-secondary-success-fg",
                    "button-secondary-success-bg",
                    "button-secondary-success-border",
                    "hover:button-secondary-success-fg_hover",
                    "hover:button-secondary-success-bg_hover",
                    "hover:button-secondary-success-border_hover",
                ],
            },
            {
                variant: ["primary", "secondary"],
                type: "success",
                class: ["focus:ring-success-shadow"],
            },
            {
                variant: "tertiary",
                type: "success",
                class: [
                    "button-tertiary-success-fg",
                    "hover:button-tertiary-success-fg_hover",
                    "hover:button-tertiary-success-bg_hover",
                ],
            },
            {
                variant: "link",
                type: "success",
                class: [
                    "button-tertiary-success-fg",
                    "hover:button-tertiary-success-fg_hover",
                ],
            },
            {
                variant: "primary",
                type: "destructive",
                class: [
                    "button-primary-error-fg",
                    "button-primary-error-bg",
                    "button-primary-error-border",
                    "hover:button-primary-error-fg_hover",
                    "hover:button-primary-error-bg_hover",
                    "hover:button-primary-error-border_hover",
                ],
            },
            {
                variant: "secondary",
                type: "destructive",
                class: [
                    "button-secondary-error-fg",
                    "button-secondary-error-bg",
                    "button-secondary-error-border",
                    "hover:button-secondary-error-fg_hover",
                    "hover:button-secondary-error-bg_hover",
                    "hover:button-secondary-error-border_hover",
                ],
            },
            {
                variant: ["primary", "secondary"],
                type: "destructive",
                class: ["focus:ring-error-shadow"],
            },
            {
                variant: "tertiary",
                type: "destructive",
                class: [
                    "button-tertiary-error-fg",
                    "hover:button-tertiary-error-fg_hover",
                    "hover:button-tertiary-error-bg_hover",
                ],
            },
            {
                variant: "link",
                type: "destructive",
                class: [
                    "button-tertiary-error-fg",
                    "hover:button-tertiary-error-fg_hover",
                ],
            },
            {
                variant: "close",
                size: "lg",
                class: ["[&>i]:h-6.5", "[&>i]:w-6.5"],
            },
            {
                icon: true,
                class: ["aspect-square", "px-0"],
            },
            {
                variant: "link",
                size: ["xs", "sm", "md", "lg", "xl", "2xl"],
                class: ["px-0", "h-auto"],
            },
        ],
        defaultVariants: {
            variant: "primary",
            type: "default",
            size: "md",
        },
    }
);
